import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, EmptyValue, Form, Modal, Spinner } from '@/components/ui';
import { serviceAreaLabel } from '../utils/equipmentDisplay';
import {
  APPROVAL_KIND_LABEL,
  vehicleTransferService,
  type ApproverPreview,
  type DestinationArea,
  type FormDefaults,
  type Transfer,
  type TransferDetail,
} from '../../../services/vehicleTransferService';

interface RequestTransferModalProps {
  vehicleId: number;
  show: boolean;
  onHide: () => void;
  onCreated: (transfer: TransferDetail) => void;
  /** Start a return of a completed temporary move: the reverse direction, prefilled. */
  returnOf?: Transfer | null;
}

/** The file types a transfer can carry (FORM-07): documents, and pictures (images only). */
const DOCUMENT_TYPES = {
  accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,application/pdf,image/*',
  text: 'Upload a PDF, Word document or image (JPG, PNG, HEIC).',
  ext: /\.(pdf|docx?|jpe?g|png|heic)$/i
};
const PICTURE_TYPES = {
  accept: '.jpg,.jpeg,.png,.heic,image/*',
  text: 'Upload an image (JPG, PNG, HEIC).',
  ext: /\.(jpe?g|png|heic)$/i
};
type FileTypes = typeof DOCUMENT_TYPES;

const areaLabel = (a: { name: string; code: string | null }) => serviceAreaLabel(a);

function fileSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(file: File): string {
  if (file.type.startsWith('image/') || /\.heic$/i.test(file.name)) return 'bi-file-earmark-image';
  if (/\.pdf$/i.test(file.name)) return 'bi-file-earmark-pdf';
  if (/\.docx?$/i.test(file.name)) return 'bi-file-earmark-word';
  return 'bi-file-earmark';
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="tw-mb-5 last:tw-mb-0">
    <h3 className="tw-mb-3 tw-border-b tw-border-line tw-pb-1 tw-text-sm tw-font-semibold tw-text-content">{title}</h3>
    {children}
  </section>
);

const ReadOnly: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div className="tw-text-sm tw-font-semibold tw-text-content-secondary">{label}</div>
    <div className="tw-text-sm tw-text-content tw-tabular-nums">{value || value === 0 ? value : <EmptyValue />}</div>
  </div>
);

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? (
    <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-danger" role="alert">
      {message}
    </p>
  ) : null;

const Required = () => (
  <span className="tw-text-danger" aria-hidden="true">
    {' '}
    *
  </span>
);

/** One optional file field: a drop zone and the list of chosen files, each removable. */
const FileDrop: React.FC<{
  label: string;
  hint: string;
  types: FileTypes;
  files: File[];
  error?: string;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
}> = ({ label, hint, types, files, error, onAdd, onRemove }) => {
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const previews = useMemo(() => files.map((f) => (f.type.startsWith('image/') ? URL.createObjectURL(f) : null)), [files]);
  useEffect(() => () => previews.forEach((u) => u && URL.revokeObjectURL(u)), [previews]);
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`Add ${label.toLowerCase()}`}
        onClick={() => input.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            input.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          onAdd(Array.from(e.dataTransfer.files));
        }}
        className={`tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-rounded-lg tw-border tw-border-dashed tw-p-4 tw-text-center tw-transition-colors ${
          dragging ? 'tw-border-primary tw-bg-primary/5' : 'tw-border-line-input tw-bg-surface-alt hover:tw-border-primary'
        }`}
      >
        <i className={`bi ${label === 'Pictures' ? 'bi-camera' : 'bi-paperclip'} tw-text-xl tw-text-content-muted`} aria-hidden="true" />
        <span className="tw-text-sm tw-font-medium tw-text-primary">Add {label}</span>
        <span className="tw-text-xs tw-text-content-muted">
          {hint} {types.text}
        </span>
        <input
          ref={input}
          type="file"
          multiple
          accept={types.accept}
          className="tw-sr-only"
          onChange={(e) => {
            onAdd(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
      </div>
      <FieldError message={error} />
      {files.length > 0 && (
        <ul className="tw-mb-0 tw-mt-3 tw-divide-y tw-divide-line tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-pl-0">
          {files.map((file, i) => (
            <li key={`${file.name}-${i}`} className="tw-flex tw-list-none tw-items-center tw-gap-3 tw-px-3 tw-py-2">
              {previews[i] ? (
                <img src={previews[i]!} alt="" className="tw-h-10 tw-w-10 tw-shrink-0 tw-rounded tw-object-cover" />
              ) : (
                <i className={`bi ${fileIcon(file)} tw-w-10 tw-shrink-0 tw-text-center tw-text-2xl tw-text-content-muted`} aria-hidden="true" />
              )}
              <div className="tw-min-w-0 tw-flex-1">
                <div className="tw-truncate tw-text-sm tw-text-content" title={file.name}>
                  {file.name}
                </div>
                <div className="tw-text-xs tw-text-content-muted">{fileSize(file.size)}</div>
              </div>
              <Button size="sm" variant="outline-secondary" aria-label={`Remove ${file.name}`} onClick={() => onRemove(i)}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

/**
 * Transfer Service Area: the form agreed with Finance (2026-11-03). The unit's details, current
 * reading and rate are read-only; the requester picks where it goes and when, then (V3-057, all
 * optional) Pictures, Accessories, Documents and one large Comments field, last, and sends it for
 * approval.
 */
const RequestTransferModal: React.FC<RequestTransferModalProps> = ({ vehicleId, show, onHide, onCreated, returnOf }) => {
  const [defaults, setDefaults] = useState<FormDefaults | null>(null);
  const [areas, setAreas] = useState<DestinationArea[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [toForemanAreaId, setToForemanAreaId] = useState('');
  const [toYardId, setToYardId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [pictures, setPictures] = useState<File[]>([]);
  const [accessories, setAccessories] = useState('');
  const [documents, setDocuments] = useState<File[]>([]);
  const [comments, setComments] = useState('');

  const [preview, setPreview] = useState<ApproverPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setLoadError(null);
    setErrors({});
    Promise.all([vehicleTransferService.formDefaults(vehicleId), vehicleTransferService.destinations()])
      .then(([d, list]) => {
        if (cancelled) return;
        setDefaults(d);
        setAreas(list);
        setFromId(String(returnOf ? returnOf.to.id : d.defaultFromServiceAreaId ?? ''));
        setToId(returnOf ? String(returnOf.from.id) : '');
        setToForemanAreaId('');
        setToYardId('');
        setEffectiveDate(d.today);
        setComments(returnOf ? `Return of transfer #${returnOf.id}.` : '');
        setPictures([]);
        setAccessories('');
        setDocuments([]);
      })
      // V3-068: a friendly sentence, never a raw error such as "Invalid unit id".
      .catch(() => !cancelled && setLoadError("The transfer form couldn't be opened for this unit. Close it and try again, or contact support."));
    return () => {
      cancelled = true;
    };
  }, [show, vehicleId, returnOf]);

  // Who it will go to, as soon as both ends are known.
  useEffect(() => {
    if (!show || !fromId || !toId || fromId === toId) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    vehicleTransferService
      .approvers(Number(fromId), Number(toId))
      .then((p) => !cancelled && setPreview(p))
      .catch((err) => !cancelled && setPreview({ approvers: [], problem: err instanceof Error ? err.message : 'Could not find the approvers' }))
      .finally(() => !cancelled && setPreviewLoading(false));
    return () => {
      cancelled = true;
    };
  }, [show, fromId, toId]);

  const destination = areas.find((a) => String(a.id) === toId);
  const foremanArea = destination?.foremanAreas.find((f) => String(f.id) === toForemanAreaId);
  const yards = foremanArea ? foremanArea.yards : [];
  const unitAreaIds = new Set((defaults?.fromAreas ?? []).map((a) => String(a.id)));
  const fromArea = defaults?.fromAreas.find((a) => String(a.id) === fromId) ?? defaults?.fromAreas[0];

  const addFiles =
    (field: 'pictures' | 'documents', types: FileTypes, set: React.Dispatch<React.SetStateAction<File[]>>) => (files: File[]) => {
      const bad = files.filter((f) => !types.ext.test(f.name));
      setErrors((e) => ({ ...e, [field]: bad.length ? `${bad.map((f) => f.name).join(', ')}: ${types.text}` : '' }));
      set((prev) => [...prev, ...files.filter((f) => types.ext.test(f.name))]);
    };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (!fromId) next.from = 'This unit has no service area to transfer from. Fix the unit first.';
    if (!toId) next.to = 'Destination service area required';
    else if (unitAreaIds.has(toId)) next.to = 'The unit is already in that service area';
    if (!toForemanAreaId) next.toForemanArea = 'Destination foreman area required';
    if (!effectiveDate) next.effectiveDate = 'Date of Move required';
    return next;
  };

  const submit = async () => {
    if (!defaults) return;
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    if (previewLoading || !preview || preview.problem) return;
    setSaving(true);
    try {
      const created = await vehicleTransferService.request(
        {
          vehicleId,
          fromServiceAreaId: Number(fromId),
          toServiceAreaId: Number(toId),
          toForemanAreaId: Number(toForemanAreaId),
          toYardId: toYardId ? Number(toYardId) : null,
          effectiveDate,
          accessories,
          comments,
          returnOfTransferId: returnOf?.id ?? null,
        },
        { pictures, documents }
      );
      onCreated(created);
    } catch (err) {
      setErrors({ _form: err instanceof Error ? err.message : 'The request could not be sent. Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = defaults?.meterUnit === 'HR' ? 'Hours' : 'Mileage';
  const reading =
    defaults?.meterOut != null ? `${defaults.meterOut.toLocaleString('en-CA')} ${defaults.meterUnit === 'HR' ? 'hours' : 'km'}` : null;
  const rate =
    defaults?.rate != null
      ? `${defaults.rate.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', currencyDisplay: 'narrowSymbol' })} / h`
      : null;
  const blocked = Boolean(defaults?.openTransferId) || Boolean(preview?.problem);

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{returnOf ? 'Return Transfer' : 'Transfer Service Area'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadError && <Alert variant="danger">{loadError}</Alert>}
        {!defaults && !loadError && (
          <div className="tw-py-8 tw-text-center">
            <Spinner role="status" />
          </div>
        )}
        {defaults && (
          <div>
            {defaults.openTransferId && (
              <Alert variant="warning">
                This unit already has an open transfer (#{defaults.openTransferId}). Finish or cancel it first.
              </Alert>
            )}

            <Section title="Equipment Information">
              <div className="tw-grid tw-grid-cols-2 tw-gap-4 md:tw-grid-cols-4">
                <ReadOnly label="Unit #" value={defaults.unit.unitNumber} />
                <ReadOnly label={unitLabel} value={reading} />
                <ReadOnly label="Rate" value={rate} />
                <ReadOnly label="Year" value={defaults.unit.year} />
                <ReadOnly label="Make" value={defaults.unit.make} />
                <ReadOnly label="Model" value={defaults.unit.model} />
                <div className="tw-col-span-2">
                  <ReadOnly label="VIN" value={defaults.unit.vin} />
                </div>
                <div className="tw-col-span-2 md:tw-col-span-4">
                  <ReadOnly label="Description" value={defaults.unit.description} />
                </div>
              </div>
            </Section>

            <Section title="Move">
              <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
                <div>
                  <ReadOnly label="Current Service Area" value={fromArea ? areaLabel(fromArea) : null} />
                  <FieldError message={errors.from} />
                </div>
                <Form.Group controlId="transfer-date">
                  <Form.Label>
                    Date of Move
                    <Required />
                  </Form.Label>
                  <Form.Control type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
                  <FieldError message={errors.effectiveDate} />
                </Form.Group>
                <Form.Group controlId="transfer-to">
                  <Form.Label>
                    To Service Area
                    <Required />
                  </Form.Label>
                  <Form.Select
                    value={toId}
                    onChange={(e) => {
                      setToId(e.target.value);
                      setToForemanAreaId('');
                      setToYardId('');
                    }}
                  >
                    <option value="">Select a Service Area</option>
                    {areas
                      .filter((a) => !unitAreaIds.has(String(a.id)))
                      .map((a) => (
                        <option key={a.id} value={a.id}>
                          {areaLabel(a)}
                        </option>
                      ))}
                  </Form.Select>
                  <FieldError message={errors.to} />
                </Form.Group>
                <Form.Group controlId="transfer-foreman-area">
                  <Form.Label>
                    To Foreman Area
                    <Required />
                  </Form.Label>
                  <Form.Select
                    value={toForemanAreaId}
                    disabled={!destination}
                    onChange={(e) => {
                      setToForemanAreaId(e.target.value);
                      setToYardId('');
                    }}
                  >
                    <option value="">{destination ? 'Select a Foreman Area' : 'Choose the Service Area First'}</option>
                    {(destination?.foremanAreas ?? []).map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </Form.Select>
                  <FieldError message={errors.toForemanArea} />
                </Form.Group>
                {yards.length > 0 && (
                  <Form.Group controlId="transfer-yard">
                    <Form.Label>To Yard</Form.Label>
                    <Form.Select value={toYardId} onChange={(e) => setToYardId(e.target.value)}>
                      <option value="">Leave Unchanged</option>
                      {yards.map((y) => (
                        <option key={y.id} value={y.id}>
                          {y.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
              </div>
            </Section>

            <Section title="Approvals">
              {!toId ? (
                <p className="tw-mb-0 tw-text-sm tw-text-content-muted">Choose where the unit is going to see who approves it.</p>
              ) : previewLoading || !preview ? (
                <p className="tw-mb-0 tw-text-sm tw-text-content-muted">Finding the approvers…</p>
              ) : (
                <>
                  <ul className="tw-mb-2 tw-grid tw-gap-2 tw-pl-0 md:tw-grid-cols-3">
                    {preview.approvers.map((a) => (
                      <li key={a.kind} className="tw-list-none tw-rounded-lg tw-border tw-border-line tw-bg-surface-alt tw-px-3 tw-py-2">
                        <div className="tw-text-xs tw-text-content-muted">
                          {APPROVAL_KIND_LABEL[a.kind]}
                          {a.areaLabel ? ` · ${a.areaLabel}` : ''}
                        </div>
                        <div className="tw-text-sm tw-font-medium">
                          {a.user ? `${a.user.name} ${a.user.lastName}` : <span className="tw-text-danger">Nobody set</span>}
                        </div>
                        {a.autoApproved && <div className="tw-text-xs tw-text-content-secondary">You: approved by sending this</div>}
                      </li>
                    ))}
                  </ul>
                  {preview.problem ? (
                    <Alert variant="danger" className="tw-mb-0">
                      {preview.problem}
                    </Alert>
                  ) : (
                    <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
                      Once everyone approves, Finance is emailed the record. The unit moves in WRAP when Finance marks the transfer complete.
                    </p>
                  )}
                </>
              )}
            </Section>

            <Section title="Pictures">
              <FileDrop
                label="Pictures"
                hint="Optional. The unit as it leaves."
                types={PICTURE_TYPES}
                files={pictures}
                error={errors.pictures}
                onAdd={addFiles('pictures', PICTURE_TYPES, setPictures)}
                onRemove={(i) => setPictures((prev) => prev.filter((_, j) => j !== i))}
              />
            </Section>

            <Section title="Accessories">
              <Form.Control
                as="textarea"
                rows={2}
                aria-label="Accessories"
                placeholder="What Goes With the Unit, e.g. Buckets or Plows"
                value={accessories}
                onChange={(e) => setAccessories(e.target.value)}
              />
            </Section>

            <Section title="Documents">
              <FileDrop
                label="Documents"
                hint="Optional. Warranty or unit files."
                types={DOCUMENT_TYPES}
                files={documents}
                error={errors.documents}
                onAdd={addFiles('documents', DOCUMENT_TYPES, setDocuments)}
                onRemove={(i) => setDocuments((prev) => prev.filter((_, j) => j !== i))}
              />
            </Section>

            <Section title="Comments">
              <Form.Control
                as="textarea"
                rows={6}
                aria-label="Comments"
                placeholder="Anything Else Finance and the Receiving Area Should Know"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
            </Section>

            {errors._form && (
              <Alert variant="danger" className="tw-mb-0 tw-mt-4">
                {errors._form}
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          {loadError ? 'Close' : 'Cancel'}
        </Button>
        {/* V3-068: when the form can't be used, there is nothing to send. */}
        {!loadError && (
        <Button
          onClick={submit}
          disabled={saving || !defaults || blocked}
          disabledReason={blocked ? (defaults?.openTransferId ? 'This unit already has an open transfer.' : preview?.problem ?? undefined) : undefined}
        >
          {saving ? 'Sending…' : 'Send for Approval'}
        </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default RequestTransferModal;
